/**
 * Guide Me — per-plugin metadata.
 *
 * This module holds **only** the static, lightweight per-plugin info that
 * the Experience shell needs to decide whether to show the hint pill, the
 * menu, or a route-specific guide button:
 *
 *   • plugin label / icon
 *   • where the prose user-guides live for this plugin
 *   • route → guide-page mappings
 *
 * Tour bodies, summaries, and route→tour matching live in
 * {@link ../tourCatalog} and are loaded lazily on intent (route activation,
 * menu open, play click). Nothing in this file imports a tour body, so
 * adding tours has zero impact on the initial bundle size.
 */

import {
  findTourSummaryFor,
  listTourSummariesForPlugin,
  getTourSummaryById,
} from './tourCatalog';
import type { TourSummary } from './tourCatalog';

export type { TourSummary };

export interface RouteGuide {
  /**
   * SPA route prefix that triggers this guide.
   * Matching is prefix-based (most-specific match wins).
   */
  route: string;
  /**
   * Guide filename relative to the plugin's `guideFolder`
   * (e.g. `"people-management.html"`).
   */
  guide: string;
}

export interface PluginGuideEntry {
  /** The plugin id as used in the manifest (e.g. "identity-core"). */
  pluginId: string;
  /** Human-readable name shown in the Guide Me menu header. */
  pluginLabel: string;
  /** Optional emoji / icon shown next to the plugin name in the menu header. */
  pluginIcon?: string;
  /** Folder under `empowernow_customer_trainings/user-guides/`. */
  guideFolder: string;
  /** Filename of the plugin's "hub" guide (relative to guideFolder). */
  defaultGuide?: string;
  /**
   * Per-route guide overrides. Matched by longest-prefix so
   * `/identity-core/people/:id` beats `/identity-core/people` which
   * beats `/identity-core`.
   */
  routeGuides?: RouteGuide[];
}

const PLUGINS: ReadonlyArray<PluginGuideEntry> = [
  {
    pluginId: 'identity-core',
    pluginLabel: 'Identity Core',
    pluginIcon: '👤',
    guideFolder: 'identity-core',
    defaultGuide: 'index.html',
    routeGuides: [
      { route: '/identity-core/idp-users',           guide: 'idp-user-management.html' },
      { route: '/identity-core/people',              guide: 'people-management.html' },
      { route: '/identity-core/accounts/orphans',    guide: 'accounts-orphan-detection.html' },
      { route: '/identity-core/accounts',            guide: 'accounts-orphan-detection.html' },
      { route: '/identity-core/claim-role-mapping',  guide: 'claim-role-mapping.html' },
      { route: '/identity-core/access-assignments',  guide: 'access-assignments.html' },
      { route: '/identity-core/collections',         guide: 'collections.html' },
    ],
  },
  {
    pluginId: 'clients-center',
    pluginLabel: 'OAuth Clients',
    pluginIcon: '🔐',
    guideFolder: 'oauth-clients',
    defaultGuide: 'index.html',
    routeGuides: [
      { route: '/clients-center/register', guide: 'register-a-client.html' },
      { route: '/clients-center',          guide: 'client-configuration.html' },
    ],
  },
  {
    pluginId: 'token-studio',
    pluginLabel: 'Token Studio',
    pluginIcon: '🎫',
    guideFolder: 'token-studio',
    defaultGuide: 'index.html',
    routeGuides: [
      { route: '/token-studio/personal', guide: 'personal-access-tokens.html' },
      { route: '/token-studio/issuer',   guide: 'issuer-access-tokens.html' },
    ],
  },
  {
    pluginId: 'agent-hub',
    pluginLabel: 'AI Agents',
    pluginIcon: '🤖',
    guideFolder: 'agent-hub',
    defaultGuide: 'index.html',
    routeGuides: [
      { route: '/agents/discovery',  guide: 'index.html' },
      { route: '/agents/delegations', guide: 'delegations.html' },
      { route: '/agents/analytics', guide: 'agent-analytics.html' },
      { route: '/agents',          guide: 'agent-gallery.html' },
    ],
  },
  /* ── Home dashboard (host-app page, not a plugin) ─────────────── */
  {
    pluginId: 'home',
    pluginLabel: 'Home',
    pluginIcon: '🏠',
    guideFolder: 'home',
  },
  /* ── Admin plugins ────────────────────────────────────────────── */
  {
    pluginId: 'app-admin',
    pluginLabel: 'App Admin',
    pluginIcon: '🏢',
    guideFolder: 'app-admin',
  },
  {
    pluginId: 'approval-admin',
    pluginLabel: 'Approvals',
    pluginIcon: '✅',
    guideFolder: 'approval-admin',
  },
  {
    pluginId: 'azure-app-admin',
    pluginLabel: 'Azure App Admin',
    pluginIcon: '☁️',
    guideFolder: 'azure-app-admin',
  },
  {
    pluginId: 'collections-admin',
    pluginLabel: 'Collections Admin',
    pluginIcon: '📚',
    guideFolder: 'collections-admin',
  },
  {
    pluginId: 'crud-config-admin',
    pluginLabel: 'CRUD Config',
    pluginIcon: '⚙️',
    guideFolder: 'crud-config-admin',
  },
  {
    pluginId: 'entraid-admin',
    pluginLabel: 'EntraID Admin',
    pluginIcon: '🆔',
    guideFolder: 'entraid-admin',
  },
  {
    pluginId: 'github-admin',
    pluginLabel: 'GitHub Admin',
    pluginIcon: '🐙',
    guideFolder: 'github-admin',
  },
  {
    pluginId: 'idp-config-admin',
    pluginLabel: 'IdP Config',
    pluginIcon: '🔑',
    guideFolder: 'idp-config-admin',
  },
  {
    pluginId: 'ldap-admin',
    pluginLabel: 'LDAP Admin',
    pluginIcon: '📂',
    guideFolder: 'ldap-admin',
  },
  {
    pluginId: 'mcp-admin',
    pluginLabel: 'MCP Admin',
    pluginIcon: '🔧',
    guideFolder: 'mcp-admin',
  },
  {
    pluginId: 'pdp-config-admin',
    pluginLabel: 'PDP Config',
    pluginIcon: '🛡️',
    guideFolder: 'pdp-config-admin',
  },
  {
    pluginId: 'res-admin-classic',
    pluginLabel: 'Resource Admin (Classic)',
    pluginIcon: '🗂️',
    guideFolder: 'res-admin-classic',
  },
  {
    pluginId: 'routes-admin',
    pluginLabel: 'Routes Admin',
    pluginIcon: '🔀',
    guideFolder: 'routes-admin',
  },
  {
    pluginId: 'sap-ias-admin',
    pluginLabel: 'SAP IAS Admin',
    pluginIcon: '🔐',
    guideFolder: 'sap-ias-admin',
  },
  {
    pluginId: 'sap-rfc-admin',
    pluginLabel: 'SAP AccessPulse',
    pluginIcon: '📊',
    guideFolder: 'sap-rfc-admin',
  },
  {
    pluginId: 'vds-admin',
    pluginLabel: 'VDS Admin',
    pluginIcon: '🌐',
    guideFolder: 'vds-admin',
  },
];

export const PLUGIN_GUIDE_MAP: ReadonlyMap<string, PluginGuideEntry> = new Map(
  PLUGINS.map((p) => [p.pluginId, p]),
);

/**
 * Base URL the BFF proxies the customer-trainings `user-guides/` directory
 * under. Same-origin so the menu can render the prose guide in an iframe
 * without CSP / cookie issues.
 */
export const TRAINING_GUIDES_BASE = '/api/training/user-guides';

export function buildGuideUrl(path: string | undefined | null): string | null {
  if (!path) return null;
  const trimmed = path.trim().replace(/^\/+/, '');
  if (!trimmed) return null;
  return `${TRAINING_GUIDES_BASE}/${trimmed}`;
}

export function getPluginGuideUrl(entry: PluginGuideEntry | null): string | null {
  if (!entry || !entry.guideFolder) return null;
  const file = entry.defaultGuide ?? 'index.html';
  return buildGuideUrl(`${entry.guideFolder}/${file}`);
}

/**
 * Returns the most relevant guide URL for the current SPA `pathname`.
 * Walks the plugin's `routeGuides` first, then falls back to the
 * route-matched tour's `guidePath`, then to the plugin default.
 */
export function getRouteGuideUrl(
  entry: PluginGuideEntry | null,
  pathname: string,
): string | null {
  if (!entry || !entry.guideFolder) return null;

  if (entry.routeGuides && entry.routeGuides.length > 0) {
    const sorted = [...entry.routeGuides].sort((a, b) => b.route.length - a.route.length);
    for (const { route, guide } of sorted) {
      if (pathname === route || pathname.startsWith(`${route}/`)) {
        return buildGuideUrl(`${entry.guideFolder}/${guide}`);
      }
    }
  }

  // Cheap manifest lookup — does NOT load the tour body.
  const tour = findTourSummaryFor(entry.pluginId, pathname);
  if (tour?.guidePath) return buildGuideUrl(tour.guidePath);

  return getPluginGuideUrl(entry);
}

/* ─────────────────────── tour-summary re-exports ──────────────────── */
/* Thin pass-throughs so callers can keep importing from this module.   */

/**
 * All authored tour summaries registered for a plugin, in declaration
 * order. **Synchronous and bundle-cheap** — bodies are not loaded.
 */
export function getToursForPlugin(pluginId: string | null): TourSummary[] {
  return listTourSummariesForPlugin(pluginId);
}

/**
 * Catalog summary lookup. Synchronous; does NOT load the tour body.
 * Use {@link tourCatalog.loadTourById} when you need the parsed steps.
 */
export function getCatalogTourById(tourId: string): TourSummary | null {
  return getTourSummaryById(tourId);
}

/**
 * Find the most relevant tour for a plugin + path. Prefers a route-prefix
 * match, then a `routePattern` regex match, then falls back to the
 * plugin's first registered tour. Synchronous; bodies are not loaded.
 */
export function findTourFor(pluginId: string | null, pathname: string): TourSummary | null {
  return findTourSummaryFor(pluginId, pathname, { fallbackToFirst: true });
}

export function hasTourFor(pluginId: string | null, pathname: string): boolean {
  return findTourFor(pluginId, pathname) !== null;
}

export function getGuideEntry(pluginId: string | null): PluginGuideEntry | null {
  if (!pluginId) return null;
  return PLUGIN_GUIDE_MAP.get(pluginId) ?? null;
}
